import React, { useMemo, useRef, useEffect, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styles from "./MapComponent.module.css";
import MapControls from "./MapControls";
import MapMarkers from "./MapMarkers";
import MapCouriers from "./MapCouriers";
import ReceiptInfoPanel from "./ReceiptInfoPanel";
import { getStatusColor } from "./mapUtils";

/**
 * MapComponent умеет центрировать карту с учетом того,
 * какую долю ширины модалки она сейчас занимает (splitPercent).
 */
export default function MapComponent({
  center = [55.751244, 37.618423],
  zoom = 11,
  receipts = [],
  courierLocations = [],
  allowedUsersLocations = {},
  userLocation,
  onMarkerClick,
  highlightPosition,
  selectedReceipt,
  splitPercent = 60, // 🧩 новое свойство
}) {
  const mapRef = useRef(null);
  const wrapperRef = useRef(null);
  const [showFullRoute, setShowFullRoute] = useState(false);

  // --- валидные точки ---
  const validReceipts = useMemo(() => {
    return (receipts || [])
      .map((r) => ({
        ...r,
        latitude: parseFloat(r.latitude),
        longitude: parseFloat(r.longitude),
      }))
      .filter(
        (r) => Number.isFinite(r.latitude) && Number.isFinite(r.longitude)
      );
  }, [receipts]);

  // === реакция на изменение splitPercent ===
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.invalidateSize();
  }, [splitPercent]);

  // === центрирование с учетом splitPercent ===
  useEffect(() => {
    if (!highlightPosition || !mapRef.current) return;

    const map = mapRef.current;
    const [lat, lng] = highlightPosition;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const mapSize = map.getSize();
    const mapWidth = mapSize.x;

    // вычисляем смещение центра, если карта узкая
    const visualCenterShift = (0.5 - splitPercent / 100 / 2) * mapWidth;
    const targetPoint = map.latLngToContainerPoint([lat, lng]);
    const shiftedPoint = L.point(targetPoint.x - visualCenterShift, targetPoint.y);
    const newLatLng = map.containerPointToLatLng(shiftedPoint);

    console.groupCollapsed("📍 Перемещение карты");
    console.log("splitPercent:", splitPercent);
    console.log("mapWidth:", mapWidth);
    console.log("visualCenterShift(px):", visualCenterShift.toFixed(1));
    console.log("targetPoint:", targetPoint);
    console.log("shiftedPoint:", shiftedPoint);
    console.log("newLatLng:", newLatLng);
    console.groupEnd();

    map.flyTo(newLatLng, map.getZoom(), { animate: true, duration: 0.7 });
  }, [highlightPosition, splitPercent]);

  // === пересчет размеров контейнера ===
  useEffect(() => {
    if (!wrapperRef.current || !mapRef.current) return;
    const observer = new ResizeObserver(() => {
      mapRef.current.invalidateSize();
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className={styles.mapWrapper}>
      <MapControls
        showFullRoute={showFullRoute}
        onToggleRoute={() => setShowFullRoute((p) => !p)}
      />

      <MapContainer
        center={center}
        zoom={zoom}
        whenCreated={(m) => (mapRef.current = m)}
        style={{ height: "100%", width: "100%" }}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution=""
        />

        <MapMarkers
          receipts={validReceipts}
          showFullRoute={showFullRoute}
          onMarkerClick={onMarkerClick}
          selectedReceipt={selectedReceipt}
        />

        <MapCouriers
          courierLocations={courierLocations}
          allowedUsersLocations={allowedUsersLocations}
          userLocation={userLocation}
        />
      </MapContainer>

      <ReceiptInfoPanel
        receipt={selectedReceipt}
        getStatusColor={getStatusColor}
      />

      {/* 🎯 прицел для проверки */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 2000,
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "24px",
            height: "1px",
            background: "rgba(255,0,0,0.7)",
            left: "-12px",
            top: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "1px",
            height: "24px",
            background: "rgba(255,0,0,0.7)",
            left: 0,
            top: "-12px",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "6px",
            height: "6px",
            background: "red",
            borderRadius: "50%",
            top: "-3px",
            left: "-3px",
          }}
        />
      </div>
    </div>
  );
}
